import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Briefcase, ArrowRight, CheckCircle2, Sparkles, Loader2 } from "lucide-react";

const TalentSignup = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    bio: "",
    skills: "",
    linkedin_url: "",
    portfolio_url: "",
    city: "",
    state: "",
    talent_visible: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      setError("Please provide your name, email, and a password of at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const skills = form.skills.split(",").map((s) => s.trim()).filter(Boolean);
      const res = await fetch(`${API}/talent/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, skills }),
      });
      let data = {};
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data?.detail || `Signup failed (HTTP ${res.status})`);
      setUser(data);
      toast.success(`Welcome, ${data.name}! You're in the Talent DB.`);
      navigate("/dashboard?onboarding=talent");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface-app" data-testid="talent-signup-page">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 py-14 relative">
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3">Talent Database</p>
            <h1 className="text-3xl sm:text-5xl font-heading font-bold leading-tight">
              Get on employers' radar — without enrolling in the Academy
            </h1>
            <p className="text-slate-300 mt-4 max-w-2xl">
              Create a free Talent Database profile in under a minute. You'll be able to upload your resume,
              add your skills, and choose whether to be visible to vetted employers.
            </p>
          </div>
        </section>

        {/* Two-col: form + value props */}
        <section className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_360px] gap-8">
          <Card className="border border-slate-200">
            <CardContent className="p-6 sm:p-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm mb-4" data-testid="talent-signup-error">
                  {error}
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ts-name">Full name *</Label>
                    <Input id="ts-name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="ts-name" />
                  </div>
                  <div>
                    <Label htmlFor="ts-email">Email *</Label>
                    <Input id="ts-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="ts-email" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="ts-password">Choose a password *</Label>
                    <Input id="ts-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} minLength={6} required data-testid="ts-password" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="ts-bio">Short bio</Label>
                    <Textarea id="ts-bio" rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="1-2 sentences about you, your background, what you're looking for." data-testid="ts-bio" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="ts-skills">Skills (comma separated)</Label>
                    <Input id="ts-skills" value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="e.g. QA testing, JavaScript, attention to detail" data-testid="ts-skills" />
                  </div>
                  <div>
                    <Label htmlFor="ts-linkedin">LinkedIn URL</Label>
                    <Input id="ts-linkedin" value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://www.linkedin.com/in/…" data-testid="ts-linkedin" />
                  </div>
                  <div>
                    <Label htmlFor="ts-portfolio">Portfolio URL</Label>
                    <Input id="ts-portfolio" value={form.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} placeholder="https://…" data-testid="ts-portfolio" />
                  </div>
                  <div>
                    <Label htmlFor="ts-city">City</Label>
                    <Input id="ts-city" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="ts-city" />
                  </div>
                  <div>
                    <Label htmlFor="ts-state">State</Label>
                    <Input id="ts-state" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="ts-state" />
                  </div>
                </div>

                <label className="flex items-start gap-2 text-sm text-slate-700 pt-2">
                  <input type="checkbox" checked={form.talent_visible} onChange={(e) => set("talent_visible", e.target.checked)} className="mt-1" data-testid="ts-visible" />
                  <span>
                    Make my profile visible to employers in the Talent Database now.
                    <span className="block text-xs text-slate-500 mt-0.5">You can change this anytime in your profile.</span>
                  </span>
                </label>

                <Button type="submit" disabled={loading} className="btn-primary w-full h-11" data-testid="ts-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Create Talent profile <ArrowRight className="w-4 h-4 ml-1" /></>)}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Already have an account?{" "}
                  <Link to="/login" className="text-[#1E3A8A] hover:underline" data-testid="ts-existing-link">Sign in instead</Link>
                </p>
              </form>
            </CardContent>
          </Card>

          <aside className="space-y-4">
            <Card className="border border-[#FFD507]/40 bg-[#FFD507]/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-[#0F1B3D] font-heading font-bold">
                  <Briefcase className="w-5 h-5" /> Talent profile only
                </div>
                <p className="text-sm text-slate-700 mt-2">No Academy enrollment, no $99 fee, no application review. Just a profile.</p>
                <ul className="text-sm text-slate-700 mt-4 space-y-2">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Upload your resume after signup</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Take the Working Style Quiz</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Toggle employer visibility anytime</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Become part of the Aspiritech community</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-slate-900 font-heading font-bold">
                  <Sparkles className="w-5 h-5 text-[#FFD507]" /> Want the full Academy?
                </div>
                <p className="text-sm text-slate-600 mt-2">Career training, certifications, mentorship, and grant-funded tuition.</p>
                <Link to="/apply" className="inline-flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline mt-3 font-semibold" data-testid="ts-enroll-link">
                  Start Academy enrollment instead <ArrowRight className="w-3 h-3" />
                </Link>
              </CardContent>
            </Card>
          </aside>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TalentSignup;
